import * as React from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

type ZoomCardProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
};

/**
 * ZoomCard
 * One Popover root with slot-based API.
 * Usage:
 *  <ZoomCard>
 *    <ZoomCard.Trigger>...</ZoomCard.Trigger>
 *    <ZoomCard.Content>...</ZoomCard.Content>
 *  </ZoomCard>
 */
function ZoomCardRoot({
  open: openProp,
  onOpenChange,
  children,
}: ZoomCardProps) {
  const [internal, setInternal] = React.useState(false);
  const controlled = typeof openProp === 'boolean';
  const open = controlled ? openProp : internal;

  const setOpen = (v: boolean) => {
    if (!controlled) setInternal(v);
    onOpenChange?.(v);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {children}
    </Popover>
  );
}

type TriggerProps = { children: React.ReactNode };
function ZoomCardTrigger({ children }: TriggerProps) {
  return <PopoverTrigger asChild>{children}</PopoverTrigger>;
}

type ContentProps = React.ComponentProps<typeof PopoverContent>;
function ZoomCardContent({
  className = 'w-[37.5rem] h-[10.4375rem] p-0 border-0 shadow-xl',
  align = 'center',
  side = 'top',
  sideOffset = -96,
  alignOffset = 0,
  children,
  ...rest
}: ContentProps) {
  return (
    <PopoverContent
      className={className}
      align={align}
      side={side}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      {...rest}
    >
      {children}
    </PopoverContent>
  );
}

export const ZoomCard = Object.assign(ZoomCardRoot, {
  Trigger: ZoomCardTrigger,
  Content: ZoomCardContent,
});
